// Internship.tsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Stack,
  Grow,
  Fade,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { InternshipApi } from "src/handler/accountHandler";

interface Internship {
  internshipId: number;
  internshipName: string;
  internshipDescription: string;
  internship30Days: number;
  internship45Days: number;
  internship60Days: number;
  internship90Days: number;
  internship180Days: number;
  internship270Days: number;
  internship360Days: number;
}

const DEFAULT_PRICES = {
  internship30Days: 1999,
  internship45Days: 2499,
  internship60Days: 2999,
  internship90Days: 3499,
  internship180Days: 4999,
  internship270Days: 7499,
  internship360Days: 9999,
};

const defaultInternship = (): Internship => ({
  internshipId: 0,
  internshipName: "",
  internshipDescription: "",
  internship30Days: DEFAULT_PRICES.internship30Days,
  internship45Days: DEFAULT_PRICES.internship45Days,
  internship60Days: DEFAULT_PRICES.internship60Days,
  internship90Days: DEFAULT_PRICES.internship90Days,
  internship180Days: DEFAULT_PRICES.internship180Days,
  internship270Days: DEFAULT_PRICES.internship270Days,
  internship360Days: DEFAULT_PRICES.internship360Days,
});

const Internship: React.FC = () => {
  const [internships, setInternships] = useState<Internship[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showAddDialog, setShowAddDialog] = useState<boolean>(false);
  const [newInternship, setNewInternship] = useState<Internship>(defaultInternship());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBuffer, setEditBuffer] = useState<Partial<Internship>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity?: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Normalize API response to extract internships array from wrapper { success, data: { internships: [...] } }
  const normalizeResponse = (resData: any): Internship[] => {
    if (!resData) return [];
    if (resData.success && resData.data && Array.isArray(resData.data.internships)) {
      return resData.data.internships;
    }
    if (Array.isArray(resData)) return resData;
    if (resData.internships && Array.isArray(resData.internships)) return resData.internships;
    if (resData.data && Array.isArray(resData.data)) return resData.data;
    return [];
  };

  const fetchInternships = async () => {
    try {
      setLoading(true);
      const response = await InternshipApi({ internship: {}, action: 1 });
      setInternships(normalizeResponse(response.data));
    } catch (err) {
      console.error("fetchInternships error:", err);
      setInternships([]);
      setSnackbar({ open: true, message: "Failed to load internships", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInternships();
  }, []);

  const ensureDayDefaults = (obj: Partial<Internship>): Internship => {
    return {
      internshipId: obj.internshipId ?? 0,
      internshipName: obj.internshipName ?? "",
      internshipDescription: obj.internshipDescription ?? "",
      internship30Days: Number(obj.internship30Days ?? DEFAULT_PRICES.internship30Days),
      internship45Days: Number(obj.internship45Days ?? DEFAULT_PRICES.internship45Days),
      internship60Days: Number(obj.internship60Days ?? DEFAULT_PRICES.internship60Days),
      internship90Days: Number(obj.internship90Days ?? DEFAULT_PRICES.internship90Days),
      internship180Days: Number(obj.internship180Days ?? DEFAULT_PRICES.internship180Days),
      internship270Days: Number(obj.internship270Days ?? DEFAULT_PRICES.internship270Days),
      internship360Days: Number(obj.internship360Days ?? DEFAULT_PRICES.internship360Days),
    };
  };

  const handleOpenAdd = () => {
    setNewInternship(defaultInternship());
    setShowAddDialog(true);
  };

  const handleCloseAdd = () => {
    setShowAddDialog(false);
  };

  const handleAddSave = async () => {
    const payload = ensureDayDefaults(newInternship);
    try {
      await InternshipApi({ internship: payload, action: 3 })
      setSnackbar({ open: true, message: "Internship added", severity: "success" });
      setShowAddDialog(false);
      fetchInternships();
    } catch (err) {
      console.error("handleAddSave error:", err);
      setSnackbar({ open: true, message: "Failed to add internship", severity: "error" });
    }
  };

  const startEdit = (row: Internship) => {
    setEditingId(row.internshipId);
    setEditBuffer({ ...row });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBuffer({});
  };

  const saveEdit = async () => {
    if (editingId == null) return;
    const payloadPartial = { ...(editBuffer as Partial<Internship>), internshipId: editingId };
    const payload = ensureDayDefaults(payloadPartial);
    try {
      await InternshipApi({ internship: payload, action: 3 });
      setSnackbar({ open: true, message: "Internship updated", severity: "success" });
      setEditingId(null);
      setEditBuffer({});
      fetchInternships();
    } catch (err) {
      console.error("saveEdit error:", err);
      setSnackbar({ open: true, message: "Failed to update internship", severity: "error" });
    }
  };

  return (
    <Box
      sx={{
        minHeight: "auto",
        // background gradient and subtle pattern
        background: "linear-gradient(180deg, #0f172a 0%, #0b1220 40%, #071226 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      {/* Centered container */}
      <Grow in timeout={600}>
        <Paper
          elevation={12}
          sx={{
            width: "100%",
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 10px 30px rgba(2,6,23,0.6)",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))",
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h5" sx={{ color: "#e6eef8", fontWeight: 700 }}>
                  Internship Management
                </Typography>
                <Typography variant="body2" sx={{ color: "#9fb0d6", mt: 0.5 }}>
                  Manage internship offerings, pricing and durations.
                </Typography>
              </Box>

              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenAdd}
                sx={{
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  boxShadow: "0 6px 18px rgba(14,165,233,0.18)",
                  "&:hover": { transform: "translateY(-2px)" },
                }}
              >
                Add Internship
              </Button>
            </Stack>

            {/* Content */}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 6, mb: 6 }}>
                <CircularProgress color="inherit" />
              </Box>
            ) : internships.length === 0 ? (
              <Fade in>
                <Paper
                  elevation={0}
                  sx={{
                    p: 6,
                    textAlign: "center",
                    background: "transparent",
                  }}
                >
                  <Typography variant="h6" sx={{ color: "#dbeafe" }}>
                    No internships found
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    Click <strong>Add Internship</strong> to create a new internship offering.
                  </Typography>
                </Paper>
              </Fade>
            ) : (
              <Fade in>
                <TableContainer component={Paper} sx={{ boxShadow: "none", mt: 1 }}>
                  <Table sx={{ minWidth: 800 }}>
                    <TableHead>
                      <TableRow sx={{ background: "rgba(255,255,255,0.02)" }}>
                        <TableCell sx={{ color: "#1b1c1c", fontWeight: 700 }}>Sr.No</TableCell>
                        <TableCell sx={{ color: "#1b1c1c", fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ color: "#1b1c1c", fontWeight: 700 }}>Description</TableCell>
                        <TableCell align="right" sx={{ color: "#1b1c1c", fontWeight: 700 }}>
                          30 Days
                        </TableCell>
                        <TableCell align="right" sx={{ color: "#1b1c1c", fontWeight: 700 }}>
                          45 Days
                        </TableCell>
                        <TableCell align="right" sx={{ color: "#1b1c1c", fontWeight: 700 }}>
                          60 Days
                        </TableCell>
                        <TableCell align="right" sx={{ color: "#1b1c1c", fontWeight: 700 }}>
                          90 Days
                        </TableCell>
                        <TableCell align="right" sx={{ color: "#1b1c1c", fontWeight: 700 }}>
                          180 Days
                        </TableCell>
                        <TableCell align="right" sx={{ color: "#1b1c1c", fontWeight: 700 }}>
                          270 Days
                        </TableCell>
                        <TableCell align="right" sx={{ color: "#1b1c1c", fontWeight: 700 }}>
                          360 Days
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#1b1c1c", fontWeight: 700 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {internships.map((row, idx) => {
                        const isEditing = editingId === row.internshipId;
                        return (
                          <TableRow
                            key={row.internshipId}
                            sx={{
                              "&:hover": {
                                background: "linear-gradient(90deg, rgba(14,165,233,0.03), rgba(99,102,241,0.02))",
                                transform: "translateY(-2px)",
                                transition: "all 220ms ease",
                              },
                              transition: "all 220ms ease",
                            }}
                          >
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={(editBuffer.internshipName as string) ?? ""}
                                  onChange={(e) => setEditBuffer((s) => ({ ...s, internshipName: e.target.value }))}
                                />
                              ) : (
                                <Typography sx={{ color: "#686a6a", fontWeight: 600 }}>
                                  {idx+1 || "-"}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ minWidth: 180 }}>
                              {isEditing ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={(editBuffer.internshipName as string) ?? ""}
                                  onChange={(e) => setEditBuffer((s) => ({ ...s, internshipName: e.target.value }))}
                                />
                              ) : (
                                <Typography sx={{ color: "#686a6a", fontWeight: 600 }}>
                                  {row.internshipName || "-"}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ maxWidth: 360 }}>
                              {isEditing ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={(editBuffer.internshipDescription as string) ?? ""}
                                  onChange={(e) => setEditBuffer((s) => ({ ...s, internshipDescription: e.target.value }))}
                                />
                              ) : (
                                <Typography color="text.secondary" noWrap>
                                  {row.internshipDescription || "-"}
                                </Typography>
                              )}
                            </TableCell>

                            {(
                              [
                                "internship30Days",
                                "internship45Days",
                                "internship60Days",
                                "internship90Days",
                                "internship180Days",
                                "internship270Days",
                                "internship360Days",
                              ] as const
                            ).map((field) => (
                              <TableCell key={field} align="right">
                                {isEditing ? (
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={(editBuffer as any)[field] ?? (row as any)[field] ?? ""}
                                    onChange={(e) =>
                                      setEditBuffer((s) => ({ ...s, [field]: Number(e.target.value) }))
                                    }
                                    inputProps={{ min: 0 }}
                                  />
                                ) : (
                                  <Typography sx={{ color: "#686a6a" }}>{(row as any)[field] ?? "-"}</Typography>
                                )}
                              </TableCell>
                            ))}

                            <TableCell align="center">
                              {isEditing ? (
                                <Stack direction="row" spacing={1} justifyContent="center">
                                  <IconButton color="primary" onClick={saveEdit} aria-label="save">
                                    <SaveIcon />
                                  </IconButton>
                                  <IconButton color="inherit" onClick={cancelEdit} aria-label="cancel">
                                    <CancelIcon />
                                  </IconButton>
                                </Stack>
                              ) : (
                                <IconButton color="primary" onClick={() => startEdit(row)} aria-label="edit">
                                  <EditIcon />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Fade>
            )}
          </Box>
        </Paper>
      </Grow>

      {/* Add Internship Dialog with subtle slide animation */}
      <Dialog
        open={showAddDialog}
        onClose={handleCloseAdd}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Slide}
        TransitionProps={{ timeout: 300 }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Add Internship</Typography>
          <IconButton onClick={handleCloseAdd} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Internship Name"
              fullWidth
              value={newInternship.internshipName}
              onChange={(e) => setNewInternship((s) => ({ ...s, internshipName: e.target.value }))}
            />
            <TextField
              label="Internship Description"
              fullWidth
              multiline
              minRows={2}
              value={newInternship.internshipDescription}
              onChange={(e) => setNewInternship((s) => ({ ...s, internshipDescription: e.target.value }))}
            />

            <Typography variant="subtitle2">Pricing (per duration)</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap">
              <TextField
                label="30 Days"
                type="number"
                value={newInternship.internship30Days}
                onChange={(e) => setNewInternship((s) => ({ ...s, internship30Days: Number(e.target.value) }))}
              />
              <TextField
                label="45 Days"
                type="number"
                value={newInternship.internship45Days}
                onChange={(e) => setNewInternship((s) => ({ ...s, internship45Days: Number(e.target.value) }))}
              />
              <TextField
                label="60 Days"
                type="number"
                value={newInternship.internship60Days}
                onChange={(e) => setNewInternship((s) => ({ ...s, internship60Days: Number(e.target.value) }))}
              />
              <TextField
                label="90 Days"
                type="number"
                value={newInternship.internship90Days}
                onChange={(e) => setNewInternship((s) => ({ ...s, internship90Days: Number(e.target.value) }))}
              />
              <TextField
                label="180 Days"
                type="number"
                value={newInternship.internship180Days}
                onChange={(e) => setNewInternship((s) => ({ ...s, internship180Days: Number(e.target.value) }))}
              />
              <TextField
                label="270 Days"
                type="number"
                value={newInternship.internship270Days}
                onChange={(e) => setNewInternship((s) => ({ ...s, internship270Days: Number(e.target.value) }))}
              />
              <TextField
                label="360 Days"
                type="number"
                value={newInternship.internship360Days}
                onChange={(e) => setNewInternship((s) => ({ ...s, internship360Days: Number(e.target.value) }))}
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ pr: 3, pb: 2 }}>
          <Button onClick={handleCloseAdd}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Internship;
